import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider as OidcAuthProvider } from 'react-oidc-context';
import Layout from './components/Layout.tsx';
import HomePage from './pages/HomePage';
import FeaturesPage from './pages/FeaturesPage';
import HowItWorksPage from './pages/HowItWorksPage';
import TestimonialsPage from './pages/TestimonialsPage';
import PricingPage from './pages/PricingPage';
import ContactPage from './pages/ContactPage';
import CareersPage from './pages/CareersPage';
import { BlogList } from './pages/BlogList';
import { BlogPost } from './pages/BlogPost';
import PressPage from './pages/PressPage';
import AboutUsPage from './pages/AboutUsPage';
import SignUpPage from './pages/SignUpPage';
import NotFoundPage from './pages/NotFoundPage';
import ScrollToTop from './components/ScrollToTop';
import AffiliatePage from './pages/AffiliatePage.tsx';
import { AdminProvider } from './contexts/AdminContext';
import AdminToggle from './components/AdminToggle';
import AdminLeads from './pages/AdminLeads';
import DashboardPage from './pages/DashboardPage.tsx';
import { AuthProvider } from './contexts/AuthContext.tsx';
import ProtectedRoute from './components/auth/ProtectedRoute.tsx';
import cognitoAuthConfig from './config/cognito-config.ts';
import Profile from './pages/Profile';
import UserManagement from './components/admin/UserManagement';
import ErrorBoundary from './components/ErrorBoundary';
import UpdateNotification from './components/UpdateNotification';
import { useEffect } from 'react';
import { referralTracker } from './utils/referralTracking.ts';

const theme = createTheme({
  palette: {
    primary: {
      main: '#3f51b5',
    },
    secondary: {
      main: '#f50057',
    },
    background: {
      default: '#fafafa',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
        },
      },
    },
  },
});

function App() {
  // Initialize referral tracking on app start
  useEffect(() => {
    referralTracker.initialize();
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <OidcAuthProvider {...cognitoAuthConfig}>
          <AuthProvider>
            <AdminProvider>
              <ScrollToTop />
              <Routes>
              <Route path="/" element={<Layout />}>
                <Route index element={<HomePage />} />
                <Route path="features" element={<FeaturesPage />} />
                <Route path="how-it-works" element={<HowItWorksPage />} />
                <Route path="testimonials" element={<TestimonialsPage />} />
                <Route path="pricing" element={<PricingPage />} />
                <Route path="contact" element={<ContactPage />} />
                <Route path="careers" element={<CareersPage />} />
                <Route path="blog" element={<BlogList />} />
                <Route path="blog/:slug" element={<BlogPost />} />
                <Route path="Press" element={<PressPage />} />
                <Route path="AboutUs" element={<AboutUsPage />} />
                <Route path="SignUp" element={<SignUpPage />} />
                <Route path="Affiliates" element={<AffiliatePage />} />
                <Route path="/admin/leads" element={<AdminLeads />} />
                <Route path="/admin/users" element={<UserManagement />} />
                {/* Protected Routes */}
                <Route path="dashboard" element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                } />
                
                {/* Added Profile Route */}
                <Route path="profile" element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } />
                
                <Route path="*" element={<NotFoundPage />} />
              </Route>
              </Routes>
              <AdminToggle />
              <UpdateNotification />
            </AdminProvider>
          </AuthProvider>
        </OidcAuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;